import { useTranslation } from "react-i18next";

/**
 * Resolve a translated field on a server record.
 *
 * Records ship with two parallel shapes:
 *   • `<field>`        — the source-of-truth value (English by default)
 *   • `<field>_i18n`   — `{ [langCode]: string }` map of translations
 *
 * `t(record, "field")` returns the translation matching the user's current
 * locale, falling back to the source field when the locale is missing.
 * Empty translations are treated as missing (we never want to show "" to
 * the customer because the LLM dropped a row).
 *
 * Usage:
 *   const t = useT();
 *   <h1>{t(business, "hero_headline")}</h1>
 */
export function useT() {
  const { i18n } = useTranslation();
  return function pick(record, field) {
    if (!record) return "";
    const source = record[field] ?? "";
    const lang = i18n.language || "en";
    if (lang === "en" || lang.startsWith("en-")) return source;
    const map = record[`${field}_i18n`];
    const translated = map && typeof map === "object" ? map[lang] : null;
    return (translated && String(translated).trim()) || source;
  };
}
